// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import type { GameDefinition, GameRuleSet } from '@/features/game-definitions/types';
import type { RoundUpdateGameRuleSet } from '@/services/roundUpdateMessage';

type GameWithRules = Pick<GameDefinition, 'id' | 'rules'>;

/** Display order: the authored `sortOrder`, then the id, so the list never reshuffles. */
export function sortRuleSets(ruleSets: GameRuleSet[] | null | undefined): GameRuleSet[] {
  if (!Array.isArray(ruleSets)) return [];
  return [...ruleSets]
    .filter((ruleSet) => typeof ruleSet?.id === 'string' && ruleSet.id.length > 0)
    .sort((a, b) => {
      const orderA = typeof a.sortOrder === 'number' ? a.sortOrder : Number.MAX_SAFE_INTEGER;
      const orderB = typeof b.sortOrder === 'number' ? b.sortOrder : Number.MAX_SAFE_INTEGER;
      if (orderA !== orderB) return orderA - orderB;
      return a.id.localeCompare(b.id);
    });
}

/**
 * The definition's own default. Falls back to the first entry in display order rather than
 * returning null: a game whose config forgot the `isDefault` flag should still render with
 * something selected, because an empty radio group reads as "nothing chosen" and hides the fact
 * that the round is in fact being scored some particular way.
 */
export function resolveDefaultRuleSetId(ruleSets: GameRuleSet[] | null | undefined): string | null {
  const sorted = sortRuleSets(ruleSets);
  if (sorted.length === 0) return null;
  const flagged = sorted.find((ruleSet) => ruleSet.isDefault === true);
  return (flagged ?? sorted[0]).id;
}

export function getRuleSetById(
  ruleSets: GameRuleSet[] | null | undefined,
  id: string | null | undefined
): GameRuleSet | null {
  if (typeof id !== 'string' || id.length === 0) return null;
  return sortRuleSets(ruleSets).find((ruleSet) => ruleSet.id === id) ?? null;
}

/**
 * Precedence: the round's own choice, then the tour default, then the definition default.
 *
 * Ids that the definition no longer offers are ignored at every tier, so a retired rule set
 * degrades to the default instead of leaving the picker with nothing selected. This runs at
 * render time rather than only at prefill, because the round fetch and the game-definitions
 * fetch race — a stored id routinely arrives before the rules it refers to.
 */
export function resolveSelectedRuleSetId(params: {
  ruleSets: GameRuleSet[] | null | undefined;
  roundRuleSetId?: string | null;
  tourRuleSetId?: string | null;
}): string | null {
  const { ruleSets, roundRuleSetId, tourRuleSetId } = params;
  if (getRuleSetById(ruleSets, roundRuleSetId)) return roundRuleSetId as string;
  if (getRuleSetById(ruleSets, tourRuleSetId)) return tourRuleSetId as string;
  return resolveDefaultRuleSetId(ruleSets);
}

/** Drops selections whose game is unknown or whose rule set the game no longer offers. */
export function sanitizeRuleSetSelection(
  selection: Record<string, string> | null | undefined,
  gameDefs: GameWithRules[]
): Record<string, string> {
  if (!selection) return {};
  const rulesByGameId = new Map(gameDefs.map((game) => [game.id, game.rules]));
  const sanitized: Record<string, string> = {};
  Object.entries(selection).forEach(([gameId, ruleSetId]) => {
    if (!rulesByGameId.has(gameId)) return;
    if (getRuleSetById(rulesByGameId.get(gameId), ruleSetId)) {
      sanitized[gameId] = ruleSetId;
    }
  });
  return sanitized;
}

/**
 * The `gameRuleSets` payload for a round.update.
 *
 * Only games that are actually selected AND offer more than one rule set are emitted. A game with
 * a single rule set has nothing to choose, so sending its id would store a row that can never
 * differ from the default and would have to be migrated the day that default is renamed.
 */
export function buildGameRuleSetPayload(params: {
  gameDefinitionIds: Iterable<string>;
  ruleSetByGameId: Record<string, string>;
  gameDefs: GameWithRules[];
  tourRuleSetByGameId?: Record<string, string>;
}): RoundUpdateGameRuleSet[] {
  const { gameDefinitionIds, ruleSetByGameId, gameDefs, tourRuleSetByGameId } = params;
  const gamesById = new Map(gameDefs.map((game) => [game.id, game]));
  const payload: RoundUpdateGameRuleSet[] = [];

  Array.from(gameDefinitionIds).forEach((gameId) => {
    const game = gamesById.get(gameId);
    const ruleSets = sortRuleSets(game?.rules);
    if (ruleSets.length < 2) return;

    const selected = resolveSelectedRuleSetId({
      ruleSets,
      roundRuleSetId: ruleSetByGameId[gameId],
      tourRuleSetId: tourRuleSetByGameId?.[gameId],
    });
    if (selected) {
      payload.push({ gameDefinitionId: gameId, ruleSetId: selected });
    }
  });

  return payload;
}

/**
 * The rule set the player last used for each game, newest round first.
 *
 * "Remembered" is derived from the player's own round history rather than stored as a preference:
 * it needs no new storage, follows them across devices and reinstalls, and cannot drift out of
 * step with what they actually played. Only games the round pinned a rule set for appear — a
 * round that left a game on its default says nothing about what the player prefers.
 */
export function buildLastUsedRuleSetByGameId(
  setups: { createdAt: string; games: { gameDefinitionId: string; ruleSetId?: string | null }[] }[]
): Record<string, string> {
  const lastUsed: Record<string, string> = {};
  [...setups]
    // Oldest first so a newer round overwrites an older one.
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
    .forEach((setup) => {
      setup.games.forEach((game) => {
        if (typeof game.ruleSetId === 'string' && game.ruleSetId.length > 0) {
          lastUsed[game.gameDefinitionId] = game.ruleSetId;
        }
      });
    });
  return lastUsed;
}
